import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  forwardRef,
} from "react";
import {
  computeFittedStageSize,
  TOOL_PREP_STAGE_INSET,
  useImageNaturalSize,
} from "../../../lib/toolPrepImageLayout";
import RoundLoader from "../../../../../components/ui/RoundLoader";
import ToolPrepCanvasStage from "../../../shared/Popup/ToolPrepCanvasStage";
import "./AiFeaturePreview.css";
import "./AiMaskPaintPreview.css";

const MASK_FILL = "#ef4444";

/** Circle cursor sized to brush; brush = solid ring, eraser = dashed ring. */
function buildPaintCursor(tool, brushSize) {
  const diameter = Math.max(10, Math.min(72, Math.round(brushSize)));
  const pad = 4;
  const size = diameter + pad * 2;
  const c = size / 2;
  const r = diameter / 2;
  const stroke = tool === "eraser" ? "#f5f5f5" : "#ef4444";
  const dash =
    tool === "eraser"
      ? `stroke-dasharray="${Math.max(2, Math.round(r / 3))} ${Math.max(2, Math.round(r / 3))}"`
      : "";
  const tip =
    tool === "eraser"
      ? `<rect x="${c - 3}" y="${c - 3}" width="6" height="6" rx="1" fill="#f5f5f5" stroke="#111" stroke-width="1"/>`
      : `<circle cx="${c}" cy="${c}" r="2" fill="#ef4444"/>`;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
    <circle cx="${c}" cy="${c}" r="${r}" fill="rgba(239,68,68,0.12)" stroke="${stroke}" stroke-width="1.75" ${dash}/>
    ${tip}
  </svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}") ${c} ${c}, ${
    tool === "eraser" ? "cell" : "crosshair"
  }`;
}

/**
 * Shared AI mask-paint preview: fitted image + brush/eraser overlay.
 * Brush paints a translucent red mask; eraser clears it.
 */
const AiMaskPaintPreview = forwardRef(function AiMaskPaintPreview(
  {
    imageUrl = null,
    loading = false,
    loadingLabel = "Working…",
    emptyLabel = "Upload an image to preview object removal",
    tool = "brush",
    brushSize = 28,
    paintEnabled = true,
    clearVersion = 0,
    onMaskChange = null,
  },
  ref
) {
  const rootRef = useRef(null);
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastPtRef = useRef(null);
  const naturalSize = useImageNaturalSize(imageUrl);
  const [stageSize, setStageSize] = useState({ width: 420, height: 420 });
  const [hasPaint, setHasPaint] = useState(false);

  const boxStartRef = useRef(null);
  const boxSnapshotRef = useRef(null);
  const [boxDraft, setBoxDraft] = useState(null);

  const paintCursor = useMemo(() => {
    if (!paintEnabled || !imageUrl || loading) return "default";
    if (tool === "box") return "crosshair";
    if (tool === "smart") return "default";
    return buildPaintCursor(tool, brushSize);
  }, [brushSize, imageUrl, loading, paintEnabled, tool]);


  const notifyMask = useCallback(
    (next) => {
      setHasPaint(next);
      onMaskChange?.(next);
    },
    [onMaskChange]
  );

  const clearCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    notifyMask(false);
  }, [notifyMask]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      setHasPaint(false);
      onMaskChange?.(false);
      return;
    }
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasPaint(false);
    onMaskChange?.(false);
    // Only reset when the source image or explicit clear changes — not when callbacks identity shifts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageUrl, clearVersion]);

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return undefined;

    const measure = () => {
      const aspect =
        imageUrl && naturalSize.width > 0 && naturalSize.height > 0
          ? naturalSize.width / naturalSize.height
          : 1;
      const fitted = computeFittedStageSize(
        el.clientWidth,
        el.clientHeight,
        aspect,
        { inset: TOOL_PREP_STAGE_INSET }
      );
      if (fitted) setStageSize(fitted);
    };

    measure();
    const ro =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    window.addEventListener("resize", measure);
    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [imageUrl, loading, naturalSize.width, naturalSize.height]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !imageUrl) return;
    const prevW = canvas.width;
    const prevH = canvas.height;
    const nextW = Math.max(1, Math.round(stageSize.width));
    const nextH = Math.max(1, Math.round(stageSize.height));
    if (prevW === nextW && prevH === nextH) return;

    let snapshot = null;
    if (prevW > 0 && prevH > 0 && hasPaint) {
      snapshot = document.createElement("canvas");
      snapshot.width = prevW;
      snapshot.height = prevH;
      snapshot.getContext("2d")?.drawImage(canvas, 0, 0);
    }

    canvas.width = nextW;
    canvas.height = nextH;
    const ctx = canvas.getContext("2d");
    if (ctx && snapshot) {
      ctx.drawImage(snapshot, 0, 0, nextW, nextH);
    }
  }, [stageSize.width, stageSize.height, imageUrl, hasPaint]);

  const getLocalPoint = (event) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (event.clientX - rect.left) * scaleX,
      y: (event.clientY - rect.top) * scaleY,
    };
  };

  const strokeAt = (from, to) => {
    const canvas = canvasRef.current;
    if (!canvas || !from || !to) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const radius = Math.max(1, brushSize / 2);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = Math.max(2, brushSize);

    if (tool === "eraser") {
      ctx.globalCompositeOperation = "destination-out";
      ctx.strokeStyle = "rgba(0,0,0,1)";
      ctx.fillStyle = "rgba(0,0,0,1)";
    } else {
      ctx.globalCompositeOperation = "source-over";
      ctx.strokeStyle = MASK_FILL;
      ctx.fillStyle = MASK_FILL;
    }

    ctx.beginPath();
    ctx.moveTo(from.x, from.y);
    ctx.lineTo(to.x, to.y);
    ctx.stroke();

    // Ensure a visible colored dot on click / short moves
    ctx.beginPath();
    ctx.arc(to.x, to.y, radius, 0, Math.PI * 2);
    ctx.fill();

    ctx.globalCompositeOperation = "source-over";
  };

  const sampleHasPaint = () => {
    const canvas = canvasRef.current;
    if (!canvas || canvas.width < 1 || canvas.height < 1) return false;
    const ctx = canvas.getContext("2d");
    if (!ctx) return false;
    const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
    for (let i = 3; i < data.length; i += 16) {
      if (data[i] > 8) return true;
    }
    return false;
  };

  const fillBox = (from, to, erase = false) => {
    const canvas = canvasRef.current;
    if (!canvas || !from || !to) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const x = Math.min(from.x, to.x);
    const y = Math.min(from.y, to.y);
    const w = Math.abs(to.x - from.x);
    const h = Math.abs(to.y - from.y);
    if (w < 1 || h < 1) return;
    if (erase) {
      ctx.clearRect(x, y, w, h);
      return;
    }
    ctx.globalCompositeOperation = "source-over";
    ctx.fillStyle = MASK_FILL;
    ctx.fillRect(x, y, w, h);
  };

  const handlePointerDown = (event) => {
    if (!paintEnabled || !imageUrl || loading || tool === "smart") return;
    event.preventDefault();
    const pt = getLocalPoint(event);
    if (!pt) return;
    drawingRef.current = true;
    lastPtRef.current = pt;

    if (tool === "box") {
      boxStartRef.current = pt;
      const canvas = canvasRef.current;
      if (canvas) {
        const snap = document.createElement("canvas");
        snap.width = canvas.width;
        snap.height = canvas.height;
        snap.getContext("2d")?.drawImage(canvas, 0, 0);
        boxSnapshotRef.current = snap;
      }
      setBoxDraft({ x: pt.x, y: pt.y, w: 0, h: 0 });
    } else {
      strokeAt(pt, pt);
    }

    try {
      event.currentTarget.setPointerCapture?.(event.pointerId);
    } catch {
      // ignore
    }
  };

  const handlePointerMove = (event) => {
    if (!drawingRef.current) return;
    event.preventDefault();
    const pt = getLocalPoint(event);
    if (!pt) return;

    if (tool === "box" && boxStartRef.current) {
      const start = boxStartRef.current;
      const x = Math.min(start.x, pt.x);
      const y = Math.min(start.y, pt.y);
      const w = Math.abs(pt.x - start.x);
      const h = Math.abs(pt.y - start.y);
      setBoxDraft({ x, y, w, h });

      const canvas = canvasRef.current;
      const snap = boxSnapshotRef.current;
      if (canvas && snap) {
        const ctx = canvas.getContext("2d");
        if (ctx) {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(snap, 0, 0);
          fillBox(start, pt, false);
        }
      }
      lastPtRef.current = pt;
      return;
    }

    strokeAt(lastPtRef.current || pt, pt);
    lastPtRef.current = pt;
  };

  const endStroke = (event) => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    lastPtRef.current = null;
    boxStartRef.current = null;
    boxSnapshotRef.current = null;
    setBoxDraft(null);
    notifyMask(sampleHasPaint());
    try {
      event.currentTarget.releasePointerCapture?.(event.pointerId);
    } catch {
      // ignore
    }
  };

  /** Export B/W mask PNG at source resolution (white = remove). For future API. */
  const exportMaskBlob = useCallback(async () => {
    const canvas = canvasRef.current;
    // Sample the canvas directly. hasPaint can lag a resize that keeps the pixels,
    // which leaves Generate enabled while export returns nothing.
    if (!canvas || (!hasPaint && !sampleHasPaint())) return null;
    const tw = naturalSize.width || canvas.width;
    const th = naturalSize.height || canvas.height;
    const out = document.createElement("canvas");
    out.width = tw;
    out.height = th;
    const ctx = out.getContext("2d");
    if (!ctx) return null;
    ctx.fillStyle = "#000";
    ctx.fillRect(0, 0, tw, th);
    const tmp = document.createElement("canvas");
    tmp.width = tw;
    tmp.height = th;
    const tctx = tmp.getContext("2d");
    if (!tctx) return null;
    tctx.drawImage(canvas, 0, 0, tw, th);
    const img = tctx.getImageData(0, 0, tw, th);
    const { data } = img;
    for (let i = 0; i < data.length; i += 4) {
      const v = data[i + 3] > 20 ? 255 : 0;
      data[i] = v;
      data[i + 1] = v;
      data[i + 2] = v;
      data[i + 3] = 255;
    }
    tctx.putImageData(img, 0, 0);
    ctx.drawImage(tmp, 0, 0);
    return new Promise((resolve) => {
      out.toBlob((blob) => resolve(blob), "image/png");
    });
  }, [hasPaint, naturalSize.height, naturalSize.width]);

  useImperativeHandle(
    ref,
    () => ({
      clear: clearCanvas,
      exportMaskBlob,
      hasPaint,
    }),
    [clearCanvas, exportMaskBlob, hasPaint]
  );

  const cursorClass =
    paintEnabled && imageUrl && !loading && tool !== "smart"
      ? tool === "eraser"
        ? " is-eraser"
        : tool === "box"
          ? " is-box"
          : " is-brush"
      : "";

  const hintText =
    tool === "smart"
      ? "Smart mode will auto-detect text on generate"
      : tool === "eraser"
        ? "Erase mask strokes on the image"
        : tool === "box"
          ? "Drag a box over the text to remove"
          : "Paint over the text to remove";

  return (
    <ToolPrepCanvasStage
      className="aftp-preview romp-preview"
      ref={rootRef}
      metaImageUrl={imageUrl}
      metaWidth={naturalSize.width}
      metaHeight={naturalSize.height}
    >
      {loading ? (
        <div className="aftp-preview__loader">
          <RoundLoader />
          <p>{loadingLabel}</p>
        </div>
      ) : null}
      {!loading && !imageUrl ? (
        <div
          className="aftp-preview__empty-stage"
          style={{ width: stageSize.width, height: stageSize.height }}
          aria-label="Empty preview"
        >
          <p className="aftp-preview__empty">{emptyLabel}</p>
        </div>
      ) : null}
      {imageUrl ? (
        <div
          className="aftp-preview__frame romp-preview__frame"
          style={{
            width: stageSize.width,
            height: stageSize.height,
            // Stay mounted while generating so the mask canvas still exists
            // when the request is built.
            display: loading ? "none" : undefined,
          }}
        >
          <img src={imageUrl} alt="Mask paint preview" draggable={false} />
          <canvas
            ref={canvasRef}
            className={`romp-preview__mask${cursorClass}`}
            style={{ cursor: paintCursor }}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={endStroke}
            onPointerCancel={endStroke}
            onPointerLeave={endStroke}
          />
          {boxDraft && boxDraft.w > 0 && boxDraft.h > 0 ? (
            <div
              className="romp-preview__box-draft"
              style={{
                left: boxDraft.x,
                top: boxDraft.y,
                width: boxDraft.w,
                height: boxDraft.h,
              }}
            />
          ) : null}
          {paintEnabled ? (
            <p className="romp-preview__hint">{hintText}</p>
          ) : null}
        </div>
      ) : null}
    </ToolPrepCanvasStage>
  );
});

export default AiMaskPaintPreview;
